import React, { useEffect, useState } from 'react';
import type { RegionCode } from 'google-libphonenumber';
import { LiliComboBoxWIthInput } from '../LiliComboBoxWIthInput/LiliComboBoxWIthInput';
import { useExamplePhoneNum } from '../../hooks/utilities/useExamplePhoneNum';
import { getCountries } from '../../services/country.service';
import {
  formatPhoneNum,
  getCountryPrefix,
  getEmojiFromIsoCode,
  getIsoCodeFromPhone,
  isValidNum,
} from '../../services/phoneNumber.service';
import { ReactComponent as ErrorIcon } from '../../assets/icons/svg/shared/ic-shared-error.svg';
import { ReactComponent as SearchIcon } from '../../assets/svg/search-app-center-icon.svg';

interface LiliInternationalPhoneNumberInputProps {
  id: string;
  inputValue: string;
  onInputValueChange: (val: string) => void;
  initialIsoCode?: RegionCode | null;
  onIsoCodeChange?: (isoCode: RegionCode) => void;
  fieldValidationStatus: (isValid: boolean) => void;
  isDisabled?: boolean;
  errorMessage?: string;
  onBlur?: () => void;
}

const REPLACMENT_CHAR = '_';
const DEFAULT_ISO_CODE = 'US';

export const LiliInternationalPhoneNumberInput = ({
  id,
  inputValue,
  onInputValueChange,
  isDisabled,
  errorMessage,
  initialIsoCode,
  onIsoCodeChange,
  fieldValidationStatus,
  onBlur,
}: LiliInternationalPhoneNumberInputProps) => {
  const [selectedIsoCode, setSelectedIsoCode] = useState<RegionCode | null>(initialIsoCode || DEFAULT_ISO_CODE);

  const localOnIsoCodeChange = (isoCode: RegionCode) => {
    setSelectedIsoCode(isoCode);
    onIsoCodeChange?.(isoCode);
  };

  const countries = getCountries();
  const countriesForCombox = countries.map(({ isoCode, fullName, countryCode }) => ({
    title: getEmojiFromIsoCode(isoCode),
    value: isoCode,
    itemDescription: `${fullName} (${countryCode})`,
  }));

  const mask = `${getCountryPrefix(selectedIsoCode ?? DEFAULT_ISO_CODE)} ${'_'.repeat(20)}`;
  const placeholder = useExamplePhoneNum({
    isoCode: selectedIsoCode ?? DEFAULT_ISO_CODE,
  });

  const handleInputValueChange = (val: string) => {
    const formattedVal = formatPhoneNum(val);
    onInputValueChange(formattedVal);
    fieldValidationStatus(isValidNum(formattedVal));
  };

  const handleIsoCodeChange = (isoCode: RegionCode) => {
    localOnIsoCodeChange(isoCode);
  };

  useEffect(() => {
    if (initialIsoCode) {
      onIsoCodeChange?.(initialIsoCode);
    } else {
      onIsoCodeChange?.(DEFAULT_ISO_CODE);
    }
    if (!selectedIsoCode) {
      localOnIsoCodeChange(DEFAULT_ISO_CODE);
    }
    if (!inputValue) return;
    handleInputValueChange(inputValue);
    const isoCode = getIsoCodeFromPhone(inputValue);
    if (!isoCode) return;
    localOnIsoCodeChange(isoCode);
  }, []);

  return (
    <div className='w-full'>
      <LiliComboBoxWIthInput
        id={`${id}-international-phone-num`}
        items={countriesForCombox}
        selectedComboBoxValue={selectedIsoCode ?? DEFAULT_ISO_CODE}
        onItemSelected={handleIsoCodeChange}
        onBlur={onBlur}
        maskConfig={{
          mask,
          replacement: {
            [REPLACMENT_CHAR]: /\d/,
          },
        }}
        inputValue={inputValue}
        onInputValueChange={handleInputValueChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        selectSearchPlaceholder='Search...'
        SelectSearchPrefixIcon={SearchIcon}
      />
      <div
        id={`${id}-error-container`}
        className={`flex items-top gap-[4px] ${errorMessage ? 'visible' : 'invisible'}]`}
      >
        <ErrorIcon
          className={`max-w-[14px] max-h-[14px] min-w-[14px] min-h-[14px] mt-[4px]  ${errorMessage ? 'visible' : 'invisible'}`}
        />
        <p className={`font-metricRMedium text-[14px] text-red02  ${errorMessage ? 'visible' : 'invisible'}`}>
          {errorMessage}
        </p>
      </div>
    </div>
  );
};
